import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CustomButtonGroup,
  CustomTextField,
} from "../../../../Assets/Elements";
import { useMediaQuery } from "react-responsive";
import DashboardPage from "../../Common/DashboardPage";
import { AuthContext } from "../../../../Context/AuthContextProvider";
import WindowIcon from "@mui/icons-material/Window";
import DnsIcon from "@mui/icons-material/Dns";
import TicketsGrid from "./TicketsGrid";
import TicketsList from "./TicketsList";
import Lottie from "lottie-react";
import lottie from "./../../../../Assets/Media/LottiesJSONs/Ticket.json";
import { MdSupportAgent, MdFilterList } from "react-icons/md";
import { IoTicketSharp } from "react-icons/io5";

function AllTickets() {
  const { loginUser, getTicket, tickets, fetchingTickets } =
    useContext(AuthContext);

  const [viewType, setViewType] = useState(
    () => localStorage.getItem("ticketViewType") || "Grid"
  );
  const [searchQuery, setSearchQuery] = useState(
    () => localStorage.getItem("ticketSearchQuery") || ""
  );

  const [ticketStatusFilter, setTicketStatusFilter] = useState(
    () => localStorage.getItem("ticketStatusFilter") || "All"
  );
  const [sortOption, setSortOption] = useState(
    () => localStorage.getItem("ticketSortOption") || "username"
  );

  useEffect(() => {
    localStorage.setItem("ticketViewType", viewType);
    localStorage.setItem("ticketSearchQuery", searchQuery);
    localStorage.setItem("ticketSortOption", sortOption);
    localStorage.setItem("ticketStatusFilter", ticketStatusFilter);
  }, [viewType, searchQuery, sortOption, ticketStatusFilter]);

  const filteredTickets = tickets.filter((ticket) => {
    const matchesSearch =
      ticket._id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.generator.name
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      ticket.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket._id?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus =
      ticketStatusFilter === "All" || ticket.status === ticketStatusFilter;
    return matchesSearch && matchesStatus;
  });

  const isLargeScreen = useMediaQuery({
    query: "(max-width: 1200px)",
  });

  const viewButtons = [
    {
      label: <WindowIcon />,
      value: "Grid",
      onClick: () => setViewType("Grid"),
    },
    { label: <DnsIcon />, value: "List", onClick: () => setViewType("List") },
  ];

  const statusButtons = [
    {
      label: "All",
      value: "All",
      onClick: () => setTicketStatusFilter("All"),
    },
    {
      label: "Open",
      value: "open",
      onClick: () => setTicketStatusFilter("open"),
    },
    {
      label: "Closed",
      value: "closed",
      onClick: () => setTicketStatusFilter("closed"),
    },
    {
      label: "Reopened",
      value: "reopened",
      onClick: () => setTicketStatusFilter("reopened"),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30">
      <DashboardPage>
        <div className="max-w-7xl mx-auto p-6 md:p-8 space-y-6">
          {/* Premium Page Header - Inspired by NewTicket */}
          <div className="relative overflow-hidden bg-gradient-to-br from-[#fcdb6a]/20 via-white to-[#b99653]/10 rounded-2xl shadow-lg border border-gray-100 p-8">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#fcdb6a]/30 to-[#d4af37]/20 rounded-full blur-3xl -mr-32 -mt-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-[#b99653]/20 to-[#d4af37]/10 rounded-full blur-2xl -ml-24 -mb-24"></div>
            
            <div className="relative flex items-start gap-6">
              <div className="p-4 bg-gradient-to-br from-[#b99653] to-[#d4af37] rounded-2xl shadow-lg">
                <MdSupportAgent className="w-10 h-10 text-white" />
              </div>
              <div className="flex-1">
                <h1 className="text-4xl font-bold bg-gradient-to-r from-[#b99653] via-[#d4af37] to-[#b99653] bg-clip-text text-transparent mb-2">
                  Support Tickets
                </h1>
                <p className="text-gray-600 text-lg">
                  Manage and track all your support requests in one place
                </p>
              </div>
              <Link
                to="/Dashboard/NewTicket"
                className="hidden lg:flex px-6 py-4 rounded-xl bg-gradient-to-r from-[#b99653] to-[#d4af37] text-white font-bold hover:shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-200 items-center gap-2"
              >
                <IoTicketSharp className="w-5 h-5" />
                Raise New Ticket
              </Link>
            </div>
          </div>

          {/* Filters Card - Inspired by NewTicket Form */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
            {/* Card Header */}
            <div className="relative bg-gradient-to-r from-[#fcdb6a]/20 to-[#b99653]/10 px-8 py-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <MdFilterList className="w-6 h-6 text-[#b99653]" />
                <h2 className="text-2xl font-bold text-gray-800">
                  Filter & Search
                </h2>
              </div>
            </div>

            {/* Filters Content */}
            <div className="p-8 space-y-6">
              {/* Search Field */}
              <div className="space-y-3">
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700 uppercase tracking-wide">
                  <span className="w-2 h-2 bg-[#b99653] rounded-full"></span>
                  Search Tickets
                </label>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by ID, title, description, or username..."
                  className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:border-[#b99653] focus:ring-4 focus:ring-[#b99653]/10 transition-all outline-none text-gray-900 font-medium placeholder:text-gray-400 bg-gray-50 focus:bg-white"
                />
              </div>

              {/* Status Filters & View Toggle */}
              <div className="space-y-3">
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700 uppercase tracking-wide">
                  <span className="w-2 h-2 bg-[#b99653] rounded-full"></span>
                  Filter by Status
                </label>
                <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
                  <div className="flex gap-2 flex-wrap">
                    <CustomButtonGroup
                      buttons={statusButtons}
                      filterType={ticketStatusFilter}
                    />
                  </div>

                  <div className="flex gap-3 items-center">
                    {!isLargeScreen && (
                      <CustomButtonGroup
                        buttons={viewButtons}
                        filterType={viewType}
                      />
                    )}
                    
                    <button
                      className="px-4 py-3 rounded-xl bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 flex items-center gap-2 font-bold shadow-sm hover:shadow"
                      onClick={() => getTicket(loginUser._id)}
                      disabled={fetchingTickets}
                    >
                      {fetchingTickets ? (
                        <span className="loading loading-spinner loading-sm"></span>
                      ) : (
                        <span className="text-lg">↻</span>
                      )}
                      <span className="hidden sm:inline">Refresh</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Mobile Raise Ticket Button */}
              <Link
                to="/Dashboard/NewTicket"
                className="lg:hidden flex w-full px-6 py-4 rounded-xl bg-gradient-to-r from-[#b99653] to-[#d4af37] text-white font-bold hover:shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-200 items-center justify-center gap-2"
              >
                <IoTicketSharp className="w-5 h-5" />
                Raise New Ticket
              </Link>
            </div>
          </div>

          {/* Tickets Content */}
          {tickets.length > 0 ? (
            <div className="animate-fadeIn">
              {viewType === "Grid" ? (
                <TicketsGrid tickets={filteredTickets} />
              ) : (
                <TicketsList tickets={filteredTickets} />
              )}
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-12">
              <div className="min-h-[40vh] flex justify-center items-center flex-col">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#fcdb6a]/20 to-[#d4af37]/20 blur-3xl rounded-full"></div>
                  <Lottie
                    animationData={lottie}
                    loop
                    autoplay
                    style={{
                      width: "300px",
                      height: "300px",
                      background: "transparent",
                    }}
                  />
                </div>
                <div className="text-center mt-6 space-y-4">
                  <h3 className="text-2xl font-bold text-gray-800">
                    No Tickets Yet
                  </h3>
                  <p className="text-gray-600 max-w-md">
                    You haven't raised any tickets so far. We hope everything is
                    going well with your experience.
                  </p>
                  <Link
                    to="/Dashboard/NewTicket"
                    className="inline-flex items-center gap-2 mt-4 px-6 py-4 rounded-xl bg-gradient-to-r from-[#b99653] to-[#d4af37] text-white font-bold hover:shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-200"
                  >
                    <IoTicketSharp className="w-5 h-5" />
                    Raise Your First Ticket
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </DashboardPage>
    </div>
  );
}

export default AllTickets;